import { StandardEvents } from '@shopify/events';

/**
 * "Ask us about this one." sits under Add to bag and shows only while the selected variant is unavailable.
 * Horizon morphs the variant picker, the add-to-bag button and the sticky bar on a variant change, but not other
 * blocks, so this element listens to the same product select event the sticky bar does and rewrites its own
 * WhatsApp message for the variant picked.
 */
class MsAskVariant extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#onSelect, {
      signal: this.#abort.signal,
    });
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  /** @param {Event & { promise?: Promise<any>, selectedOptions?: { value: string }[] }} event */
  #onSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail }) => {
        if (!detail || detail.newProduct) return;
        if (detail.productId && detail.productId !== this.dataset.productId) return;

        // No variant data means the picked combination does not exist, which counts as unavailable.
        const variant = detail.resource;
        const unavailable = variant == null || variant.available === false;
        this.toggleAttribute('hidden', !unavailable);
        if (!unavailable) return;

        const title = variant?.title ?? (event.selectedOptions ?? []).map((option) => option.value).join(' / ');
        this.#setMessage(title);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[ms-ask-variant] Event promise rejected:', error);
      });
  };

  /** @param {string} variantTitle */
  #setMessage(variantTitle) {
    const link = this.querySelector('a');
    const { href, message = '', product = '', single } = this.dataset;
    if (!link || !href) return;

    // A product with one default variant has no variant title worth quoting.
    const template = single === 'true' || !variantTitle ? message.replace(' in [variant]', '') : message;
    const text = template.replace('[product]', product).replace('[variant]', variantTitle);
    link.href = `${href}?text=${encodeURIComponent(text)}`;
  }
}

if (!customElements.get('ms-ask-variant')) {
  customElements.define('ms-ask-variant', MsAskVariant);
}
